import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "../components/icons";
import { Banner, Button, DataTable, Input, ListCard, PageHeader, Select, SlideForm, Textarea } from "../components/ui";
import { useSession } from "../context/Session";
import { decideApproval, dispatchVisitNotifications, toCsv } from "../lib/api";
import { supabase } from "../lib/supabase";

function useOrgRows(table, columns) {
  const { orgId } = useSession();
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  async function reload() {
    if (!orgId) return;
    setLoading(true);
    const { data, error: loadError } = await supabase.from(table).select(columns).eq("organization_id", orgId);
    if (loadError) setError(loadError.message);
    setRows(data || []);
    setLoading(false);
  }
  useEffect(() => { reload(); }, [orgId]);
  return { rows, error, loading, reload, orgId };
}

export function Approvals() {
  const { orgId, can } = useSession();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(null);

  async function load() {
    const { data, error: loadError } = await supabase.from("approvals").select("id, status, comment, visits(purpose, expected_arrival, visitors(first_name, last_name, company), employees(first_name, last_name), sites(name), visitor_types(name))").eq("organization_id", orgId).eq("status", "pending");
    if (loadError) setError(loadError.message);
    setRows(data || []);
  }
  useEffect(() => { if (orgId) load(); }, [orgId]);

  async function decide(decision) {
    setBusy(confirm.id);
    const { data, error: rpcError } = await decideApproval(confirm.id, decision, confirm.comment || decision);
    setBusy("");
    if (rpcError) { setError(rpcError.message); return; }
    if (data?.visit_id) {
      const sent = await dispatchVisitNotifications(data.visit_id);
      if (sent.error) setError(sent.error.message || "Decision saved. Email dispatch failed.");
    }
    setConfirm(null);
    load();
  }

  if (!can("visitors.approve") && !can("visitors.reject")) return <Banner>You do not have permission to decide approvals.</Banner>;
  return (
    <div>
      <PageHeader title="Approvals" subtitle="Confirm before approving or rejecting a visit." />
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      <ListCard title="Pending decisions" count={rows === null ? "Loading requests" : `${rows.length} ${rows.length === 1 ? "request" : "requests"}`}>
        <DataTable
          embedded
          loading={rows === null}
          rows={rows || []}
          empty={{ icon: "audit", title: "Queue is clear", body: "New approval requests appear here.", action: <Link to="/visitors"><Button>View visitors</Button></Link> }}
          columns={[
            { key: "visitor", label: "Visitor", render: (row) => <span className="font-semibold">{row.visits?.visitors?.first_name} {row.visits?.visitors?.last_name}</span> },
            { key: "company", label: "Company", render: (row) => row.visits?.visitors?.company || "—" },
            { key: "site", label: "Site", render: (row) => row.visits?.sites?.name || "—" },
            { key: "host", label: "Host", render: (row) => `${row.visits?.employees?.first_name || ""} ${row.visits?.employees?.last_name || ""}`.trim() || "—" },
            { key: "purpose", label: "Purpose", render: (row) => row.visits?.purpose || "—" },
            { key: "actions", label: "Actions", render: (row) => (
              <span className="flex flex-wrap gap-2">
                <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-brand-blue hover:bg-sky-50" onClick={() => setConfirm({ id: row.id, decision: "approved" })}>Approve</button>
                <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50" onClick={() => setConfirm({ id: row.id, decision: "rejected" })}>Reject</button>
              </span>
            ) },
          ]}
        />
      </ListCard>
      <SlideForm open={Boolean(confirm)} title={confirm ? `Confirm ${confirm.decision}` : "Confirm"} description="Add a comment, then confirm the decision." onClose={() => setConfirm(null)}>
        {confirm ? (
          <div className="space-y-4">
            <Input label="Comment" value={confirm.comment || ""} onChange={(e) => setConfirm({ ...confirm, comment: e.target.value })} />
            <div className="flex gap-2">
              <Button disabled={Boolean(busy)} onClick={() => decide(confirm.decision)}>{busy ? "Saving" : "Confirm"}</Button>
              <Button variant="secondary" onClick={() => setConfirm(null)}>Cancel</Button>
            </div>
          </div>
        ) : null}
      </SlideForm>
    </div>
  );
}

export function Hosts() {
  const { rows, error, loading, reload, orgId } = useOrgRows("employees", "id, first_name, last_name, email, department, job_title, is_host");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", department: "", job_title: "" });
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [open, setOpen] = useState(false);
  useEffect(() => { if (orgId) supabase.from("sites").select("id, name").eq("organization_id", orgId).then(({ data }) => setSites(data || [])); }, [orgId]);
  async function save(event) {
    event.preventDefault();
    const { error: insertError } = await supabase.from("employees").insert({ ...form, organization_id: orgId, site_id: siteId || null, is_host: true });
    if (insertError) return;
    setForm({ first_name: "", last_name: "", email: "", department: "", job_title: "" });
    setOpen(false);
    reload();
  }
  const hosts = rows.filter((row) => row.is_host !== false);
  return (
    <div>
      <PageHeader title="Hosts" subtitle="Employees who can receive visitors." actions={<Button onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add host</Button>} />
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      <SlideForm open={open} title="Add host" description="This person can be chosen when a visitor is invited." onClose={() => setOpen(false)}>
        <form onSubmit={save} className="space-y-4">
          <Input label="First name" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          <Input label="Last name" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Department" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
          <Input label="Job title" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
          <Select label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}><option value="">Any</option>{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</Select>
          <Button type="submit">Add host</Button>
        </form>
      </SlideForm>
      <ListCard title="All hosts" count={loading ? "Loading hosts" : `${hosts.length} ${hosts.length === 1 ? "host" : "hosts"}`}>
        <DataTable
          embedded
          loading={loading}
          rows={hosts}
          empty={{ icon: "staff", title: "No hosts", body: "Add the people visitors ask for.", action: <Button onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add host</Button> }}
          columns={[
            { key: "name", label: "Host", render: (row) => <span className="font-semibold">{row.first_name} {row.last_name}</span> },
            { key: "job_title", label: "Job title", render: (row) => row.job_title || "—" },
            { key: "department", label: "Department", render: (row) => row.department || "—" },
            { key: "email", label: "Email", render: (row) => row.email || "—" },
          ]}
        />
      </ListCard>
    </div>
  );
}

export function Sites() {
  const { rows, error, loading } = useOrgRows("sites", "id, name, code, address, timezone, status");
  return (
    <div>
      <PageHeader title="Sites" subtitle="Facilities for this organization. Globotech adds sites from the platform admin." />
      {error ? <Banner>{error}</Banner> : (
        <ListCard title="All sites" count={loading ? "Loading sites" : `${rows.length} ${rows.length === 1 ? "site" : "sites"}`}>
          <DataTable
            embedded
            loading={loading}
            rows={rows}
            empty={{ icon: "sites", title: "No sites yet", body: "Globotech adds a site for this organization from the platform admin." }}
            columns={[
              { key: "name", label: "Site", render: (row) => <span className="font-semibold">{row.name}</span> },
              { key: "code", label: "Code" },
              { key: "address", label: "Address", render: (row) => row.address || "—" },
              { key: "timezone", label: "Timezone" },
              { key: "status", label: "Status", render: (row) => <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">{row.status}</span> },
            ]}
          />
        </ListCard>
      )}
    </div>
  );
}

export function Kiosks() {
  const { rows, error, loading } = useOrgRows("kiosks", "id, name, status, last_seen_at, app_version, sites(name)");
  return (
    <div>
      <PageHeader title="Kiosks" subtitle="Device sign-in is bound in kiosk_devices. Globotech adds kiosks from the platform admin." />
      {error ? <Banner>{error}</Banner> : (
        <ListCard title="All kiosks" count={loading ? "Loading kiosks" : `${rows.length} ${rows.length === 1 ? "kiosk" : "kiosks"}`}>
          <DataTable
            embedded
            loading={loading}
            rows={rows}
            empty={{ icon: "kiosks", title: "No kiosks yet", body: "Globotech adds a kiosk after a site exists." }}
            columns={[
              { key: "name", label: "Kiosk", render: (row) => <span className="font-semibold">{row.name}</span> },
              { key: "site", label: "Site", render: (row) => row.sites?.name || "—" },
              { key: "status", label: "Status", render: (row) => <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">{row.status}</span> },
              { key: "app_version", label: "App version", render: (row) => row.app_version || "Unknown" },
              { key: "last_seen_at", label: "Last seen", render: (row) => row.last_seen_at || "Never" },
            ]}
          />
        </ListCard>
      )}
    </div>
  );
}

export function Contractors() {
  const { rows, error, reload, orgId, loading } = useOrgRows("contractor_companies", "id, name, contact_email, status");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [open, setOpen] = useState(false);
  async function save(event) {
    event.preventDefault();
    const { error: insertError } = await supabase.from("contractor_companies").insert({ organization_id: orgId, name, contact_email: email });
    if (insertError) return;
    setName(""); setEmail(""); setOpen(false); reload();
  }
  return (
    <div>
      <PageHeader title="Contractors" subtitle="Company records for contractor visits. This is not a permit-to-work system." actions={<Button onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add company</Button>} />
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      <SlideForm open={open} title="Add company" description="Use this company when inviting a contractor visit." onClose={() => setOpen(false)}>
        <form onSubmit={save} className="space-y-4">
          <Input label="Company" required value={name} onChange={(e) => setName(e.target.value)} />
          <Input label="Contact email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="submit">Add company</Button>
        </form>
      </SlideForm>
      <ListCard title="All companies" count={loading ? "Loading companies" : `${rows.length} ${rows.length === 1 ? "company" : "companies"}`}>
        <DataTable
          embedded
          loading={loading}
          rows={rows}
          empty={{ icon: "clients", title: "No contractor companies", body: "Add a company before inviting contractor visits.", action: <Button onClick={() => setOpen(true)}><Icon name="plus" className="h-4 w-4" /> Add company</Button> }}
          columns={[
            { key: "name", label: "Company", render: (row) => <span className="font-semibold">{row.name}</span> },
            { key: "contact_email", label: "Contact email", render: (row) => row.contact_email || "—" },
            { key: "status", label: "Status", render: (row) => <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">{row.status || "—"}</span> },
          ]}
        />
      </ListCard>
    </div>
  );
}

const OPTIONAL_EMAILS = [
  ["visit_reminder", "Visit reminders"],
  ["visitor_checked_in", "Visitor check-in confirmations"],
  ["visitor_checked_out", "Visitor check-out confirmations"],
  ["host_visitor_departed", "Host check-out notices"],
];

export function SettingsPage() {
  const { orgId, profile, session } = useSession();
  const [form, setForm] = useState({ name: "", welcome_message: "", primary_color: "#1EBDEF", support_email: "", support_phone: "" });
  const [agreement, setAgreement] = useState({ name: "Visitor agreement", title: "Visitor agreement", body_text: "" });
  const [visitorType, setVisitorType] = useState({ key: "contractor", name: "Contractor", requires_approval: true, requires_agreement: true, is_contractor: true });
  const [prefs, setPrefs] = useState({ visit_reminder: true, visitor_checked_in: true, visitor_checked_out: true, host_visitor_departed: true });
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    if (profile?.organization) setForm({
      name: profile.organization.name || "",
      welcome_message: profile.organization.welcome_message || "",
      primary_color: profile.organization.primary_color || "#1EBDEF",
      support_email: profile.organization.support_email || "",
      support_phone: "",
    });
    if (orgId && session?.user?.id) {
      supabase.from("notification_preferences").select("template_key, email_enabled").eq("organization_id", orgId).eq("user_id", session.user.id)
        .then(({ data }) => {
          const next = { visit_reminder: true, visitor_checked_in: true, visitor_checked_out: true, host_visitor_departed: true };
          (data || []).forEach((row) => { next[row.template_key] = row.email_enabled; });
          setPrefs(next);
        });
    }
  }, [profile, orgId, session?.user?.id]);
  async function saveOrg(event) {
    event.preventDefault();
    const { error: updateError } = await supabase.from("organizations").update(form).eq("id", orgId);
    setError(updateError?.message || "");
    setMessage(updateError ? "" : "Organization settings saved.");
  }
  async function publish(event) {
    event.preventDefault();
    const { data: agreementRow, error: agreementError } = await supabase.from("agreements").insert({ organization_id: orgId, name: agreement.name, kind: "visitor" }).select("id").single();
    if (agreementError) { setError(agreementError.message); return; }
    const { error: versionError } = await supabase.from("agreement_versions").insert({
      agreement_id: agreementRow.id, organization_id: orgId, version: 1, title: agreement.title, body_text: agreement.body_text, published_at: new Date().toISOString(),
    });
    setError(versionError?.message || "");
    setMessage(versionError ? "" : "Agreement published.");
  }
  return (
    <div className="max-w-3xl space-y-4">
      <PageHeader title="Settings" subtitle="Branding accepts a hex colour only. Custom CSS is not allowed." />
      {error ? <Banner>{error}</Banner> : null}
      {message ? <Banner tone="info">{message}</Banner> : null}
      <form onSubmit={saveOrg} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-base font-semibold">Branding</h2>
          <p className="mt-0.5 text-xs text-slate-500">Name, welcome message, and support contact shown in the portal.</p>
        </div>
        <Input label="Organization name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <Textarea label="Welcome message" value={form.welcome_message} onChange={(e) => setForm({ ...form, welcome_message: e.target.value })} />
        <Input label="Primary colour" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} pattern="^#[0-9A-Fa-f]{6}$" />
        <Input label="Support email" value={form.support_email} onChange={(e) => setForm({ ...form, support_email: e.target.value })} />
        <Button type="submit">Save branding</Button>
      </form>
      <form className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" onSubmit={async (event) => {
        event.preventDefault();
        if (!session?.user?.id) return;
        const rows = OPTIONAL_EMAILS.map(([template_key]) => ({
          organization_id: orgId,
          user_id: session.user.id,
          template_key,
          email_enabled: prefs[template_key] !== false,
        }));
        const { error: prefError } = await supabase.from("notification_preferences").upsert(rows, { onConflict: "organization_id,user_id,template_key" });
        setError(prefError?.message || "");
        setMessage(prefError ? "" : "Optional notification preferences saved. Security and access emails stay on.");
      }}>
        <div>
          <h2 className="text-base font-semibold">Optional email notifications</h2>
          <p className="mt-0.5 text-xs text-slate-500">Invitations, approvals, access, and emergency emails cannot be turned off.</p>
        </div>
        {OPTIONAL_EMAILS.map(([key, label]) => (
          <label key={key} className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={prefs[key] !== false} onChange={(e) => setPrefs({ ...prefs, [key]: e.target.checked })} />
            {label}
          </label>
        ))}
        <Button type="submit">Save email preferences</Button>
      </form>
      <form onSubmit={publish} className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-base font-semibold">Visitor agreement</h2>
          <p className="mt-0.5 text-xs text-slate-500">Published text is what a visitor accepts at the kiosk.</p>
        </div>
        <Input label="Name" value={agreement.name} onChange={(e) => setAgreement({ ...agreement, name: e.target.value })} />
        <Input label="Title" value={agreement.title} onChange={(e) => setAgreement({ ...agreement, title: e.target.value })} />
        <Textarea label="Agreement text" required rows={6} value={agreement.body_text} onChange={(e) => setAgreement({ ...agreement, body_text: e.target.value })} />
        <Button type="submit">Publish agreement</Button>
      </form>
      <form className="space-y-4 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" onSubmit={async (event) => {
        event.preventDefault();
        const { error: typeError } = await supabase.from("visitor_types").insert({ ...visitorType, organization_id: orgId, requires_photo: false });
        setError(typeError?.message || "");
        setMessage(typeError ? "" : "Visitor type saved.");
      }}>
        <div>
          <h2 className="text-base font-semibold">Visitor type</h2>
          <p className="mt-0.5 text-xs text-slate-500">Controls approval, agreement, and contractor rules for new visits.</p>
        </div>
        <Input label="Key" value={visitorType.key} onChange={(e) => setVisitorType({ ...visitorType, key: e.target.value })} />
        <Input label="Name" value={visitorType.name} onChange={(e) => setVisitorType({ ...visitorType, name: e.target.value })} />
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={visitorType.requires_approval} onChange={(e) => setVisitorType({ ...visitorType, requires_approval: e.target.checked })} /> Requires approval</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={visitorType.requires_agreement} onChange={(e) => setVisitorType({ ...visitorType, requires_agreement: e.target.checked })} /> Requires agreement</label>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={visitorType.is_contractor} onChange={(e) => setVisitorType({ ...visitorType, is_contractor: e.target.checked })} /> Contractor</label>
        <Button type="submit">Save visitor type</Button>
      </form>
    </div>
  );
}

export function Reports() {
  const { orgId, can } = useSession();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!orgId) return;
    supabase.from("visits").select("id, status, purpose, checked_in_at, checked_out_at, visitors(first_name, last_name, company), sites(name), employees(first_name, last_name), visitor_types(name, is_contractor)").eq("organization_id", orgId).limit(1000)
      .then(({ data, error: loadError }) => {
        if (loadError) setError(loadError.message);
        setRows(data || []);
      });
  }, [orgId]);
  if (!can("reports.read")) return <Banner>You do not have permission to view reports.</Banner>;
  const source = rows || [];
  const flat = source.map((row) => ({
    visitor: `${row.visitors?.first_name || ""} ${row.visitors?.last_name || ""}`,
    company: row.visitors?.company || "",
    site: row.sites?.name || "",
    host: `${row.employees?.first_name || ""} ${row.employees?.last_name || ""}`,
    type: row.visitor_types?.name || "",
    contractor: row.visitor_types?.is_contractor ? "yes" : "no",
    status: row.status,
    purpose: row.purpose || "",
    checked_in_at: row.checked_in_at || "",
    checked_out_at: row.checked_out_at || "",
  }));
  function download() {
    const blob = new Blob([toCsv(flat)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = "globovms-visits.csv"; link.click();
    URL.revokeObjectURL(url);
  }
  return (
    <div>
      <PageHeader title="Reports" subtitle="Visitor history, host activity, contractor visits, and duration fields. Export is capped at 1,000 rows." actions={<Button onClick={download} disabled={!flat.length}>Export CSV</Button>} />
      {error ? <Banner>{error}</Banner> : (
        <ListCard title="Visit export" count={rows === null ? "Loading visits" : `${flat.length} ${flat.length === 1 ? "visit" : "visits"}`}>
          <DataTable
            embedded
            loading={rows === null}
            rows={source}
            empty={{ icon: "visits", title: "No report rows", body: "Visits will appear here after they are created.", action: <Link to="/visitors/new"><Button><Icon name="plus" className="h-4 w-4" /> Invite visitor</Button></Link> }}
            columns={[
              { key: "visitor", label: "Visitor", render: (row) => <span className="font-semibold">{row.visitors?.first_name} {row.visitors?.last_name}</span> },
              { key: "company", label: "Company", render: (row) => row.visitors?.company || "—" },
              { key: "site", label: "Site", render: (row) => row.sites?.name || "—" },
              { key: "host", label: "Host", render: (row) => `${row.employees?.first_name || ""} ${row.employees?.last_name || ""}`.trim() || "—" },
              { key: "type", label: "Type", render: (row) => row.visitor_types?.name || "—" },
              { key: "status", label: "Status", render: (row) => <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold capitalize text-slate-700">{row.status}</span> },
            ]}
          />
        </ListCard>
      )}
    </div>
  );
}

export function AuditLogs() {
  const { orgId, can } = useSession();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!orgId) return;
    supabase.from("audit_logs").select("id, action, entity_table, created_at, metadata").eq("organization_id", orgId).order("created_at", { ascending: false }).limit(200)
      .then(({ data, error: loadError }) => {
        if (loadError) setError(loadError.message);
        setRows(data || []);
      });
  }, [orgId]);
  if (!can("audit.read")) return <Banner>You do not have permission to view audit logs.</Banner>;
  return (
    <div>
      <PageHeader title="Audit" subtitle="Append-only. These records cannot be edited in the portal." />
      {error ? <Banner>{error}</Banner> : (
        <ListCard title="Recent events" count={rows === null ? "Loading events" : `${rows.length} ${rows.length === 1 ? "event" : "events"}`}>
          <DataTable
            embedded
            loading={rows === null}
            rows={rows || []}
            empty={{ icon: "audit", title: "No audit events", body: "Lifecycle actions will be recorded here.", action: <Link to="/visitors"><Button>View visitors</Button></Link> }}
            columns={[
              { key: "created_at", label: "When", render: (row) => row.created_at ? new Date(row.created_at).toLocaleString() : "—" },
              { key: "action", label: "Action", render: (row) => <span className="font-semibold">{row.action}</span> },
              { key: "entity_table", label: "Record" },
            ]}
          />
        </ListCard>
      )}
    </div>
  );
}

export function Emergency() {
  const { orgId, can } = useSession();
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [entries, setEntries] = useState([]);
  const [error, setError] = useState("");
  useEffect(() => { if (orgId) supabase.from("sites").select("id, name").eq("organization_id", orgId).then(({ data }) => setSites(data || [])); }, [orgId]);
  async function start() {
    const { data, error: rpcError } = await supabase.rpc("start_muster", { p_site_id: siteId });
    if (rpcError) { setError(rpcError.message); return; }
    const { data: list } = await supabase.from("emergency_muster_entries").select("id, accounted, visits(visitors(first_name, last_name))").eq("session_id", data);
    setEntries(list || []);
  }
  async function mark(entry) {
    await supabase.rpc("mark_muster_entry", { p_entry_id: entry.id, p_accounted: !entry.accounted });
    setEntries(entries.map((item) => item.id === entry.id ? { ...item, accounted: !item.accounted } : item));
  }
  if (!can("emergency.manage")) return <Banner>You do not have permission to run muster.</Banner>;
  return (
    <div>
      <PageHeader title="Emergency muster" subtitle="Lists visitors currently checked in at the selected site." actions={<Button disabled={!siteId} onClick={start}>Start muster</Button>} />
      {error ? <div className="mb-4"><Banner>{error}</Banner></div> : null}
      <div className="mb-4 max-w-sm">
        <Select label="Site" value={siteId} onChange={(e) => setSiteId(e.target.value)}><option value="">Select</option>{sites.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</Select>
      </div>
      <ListCard title="People to account for" count={`${entries.length} ${entries.length === 1 ? "person" : "people"}`}>
        <DataTable
          embedded
          rows={entries}
          empty={{ icon: "bell", title: "No open muster", body: "Start a session to account for people on site." }}
          columns={[
            { key: "name", label: "Visitor", render: (entry) => <span className="font-semibold">{entry.visits?.visitors?.first_name} {entry.visits?.visitors?.last_name}</span> },
            { key: "accounted", label: "Status", render: (entry) => <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${entry.accounted ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>{entry.accounted ? "Accounted" : "Missing"}</span> },
            { key: "action", label: "Action", render: (entry) => <button type="button" className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-brand-blue hover:bg-sky-50" onClick={() => mark(entry)}>{entry.accounted ? "Mark missing" : "Mark accounted"}</button> },
          ]}
        />
      </ListCard>
    </div>
  );
}
